import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine } from "./Reveal";
import { ArrowDown, HeartHandshake } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const HERO_IMG = "/images/team-groceries.jpg";

const Hero = () => {
  const { t, lang } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-16" data-testid="hero-section">
      <div className="absolute -top-24 -right-24 w-[34rem] h-[34rem] rounded-full bg-[#C4D7D1]/50 blur-3xl" />
      <div className="absolute bottom-0 -left-32 w-[28rem] h-[28rem] rounded-full bg-[#F4A261]/15 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 w-full">
        <motion.div
          data-testid="hero-logo-banner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-[2rem] overflow-hidden border border-[#E2E8E4] shadow-[0_30px_60px_-30px_rgba(30,53,42,0.25)] mb-14"
        >
          <img
            src="/images/banner.jpg"
            alt="Living Fruit Outreach Initiative — Harvesting Hope, Educating Communities"
            data-testid="hero-logo"
            className="w-full h-auto"
          />
        </motion.div>
        <div className="grid grid-cols-12 gap-6 lg:gap-10 items-center">
        <div className="col-span-12 lg:col-span-7">
          <motion.p
            key={`kicker-${lang}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="text-xs tracking-[0.3em] uppercase text-[#5C7368] mb-8"
            data-testid="hero-kicker"
          >
            {t("hero_kicker")}
          </motion.p>
          <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#1A2F24]" data-testid="hero-headline" key={`headline-${lang}`}>
            <MaskedLine delay={0.1}>{t("hero_l1")}</MaskedLine>
            <MaskedLine delay={0.25}>{t("hero_l2a")}<em className="text-[#F4A261] not-italic font-medium">{t("hero_l2em")}</em>{t("hero_l2b")}</MaskedLine>
            <MaskedLine delay={0.4}>{t("hero_l3")}</MaskedLine>
          </h1>
          <motion.p
            key={`sub-${lang}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[#5C7368]"
            data-testid="hero-subtitle"
          >
            {t("hero_sub")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a href="#donate" data-testid="hero-donate-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#1E352A] text-[#FAF9F6] px-8 py-4 text-sm tracking-[0.12em] uppercase transition-all duration-300 hover:bg-[#F4A261] hover:text-white hover:-translate-y-0.5">
              <HeartHandshake size={16} /> {t("hero_give")}
            </a>
            <a href="#programs" data-testid="hero-programs-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[#1E352A]/25 px-8 py-4 text-sm tracking-[0.12em] uppercase text-[#1E352A] transition-colors duration-300 hover:border-[#F4A261] hover:text-[#F4A261]">
              {t("hero_programs")} <ArrowDown size={16} />
            </a>
          </motion.div>
        </div>

        <div className="col-span-12 lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[2rem] overflow-hidden shadow-[0_40px_80px_-30px_rgba(30,53,42,0.35)]"
          >
            <motion.img
              src={HERO_IMG}
              alt={t("hero_alt")}
              style={{ y: imgY }}
              className="w-full h-[32rem] object-cover scale-110"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A2F24]/30 to-transparent" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9 }}
            className="absolute -bottom-6 -left-6 bg-[#FAF9F6] border border-[#E2E8E4] rounded-2xl px-6 py-4 shadow-lg"
            data-testid="hero-badge"
          >
            <p className="font-display text-2xl text-[#1E352A]">{t("hero_badge_t")}</p>
            <p className="text-xs tracking-[0.2em] uppercase text-[#5C7368] mt-1">{t("hero_badge_s")}</p>
          </motion.div>
        </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
